import { useEffect, useState } from 'react';

import { Bulletin, bulletinboardsApi } from 'apis/bulletinboards';
import { useDixaMessengerApi } from 'components/settings/components/sections/dixaMessenger/api/apiProvider';

type UseLoadBulletinReturn = {
  /** Is the bulletin being fetched */
  isFetching: boolean;
  /** Has fetching the bulletin failed */
  hasError: boolean;
  /** The bulletin board id that the loaded bulletin belongs to */
  bulletinBoardId: string | null;
  /** The loaded bulletin */
  bulletin: Bulletin | null;
};

/**
 * From the messengerId and bulletinId, fetch the correct bulletin
 *
 * @param param0
 * @returns UseLoadBulletinReturn
 */
export function useLoadBulletin({
  messengerId,
  bulletinId,
}: {
  messengerId: string;
  bulletinId: string;
}): UseLoadBulletinReturn {
  const [isFetching, setIsFetching] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [boardId, setBoardId] = useState<string | null>(null);
  const messengerApi = useDixaMessengerApi();

  const [
    fetchBulletin,
    { data: bulletinApiData, isFetching: fetchBulletinIsFetching, isError },
  ] = bulletinboardsApi.useLazyReadBulletinQuery();

  useEffect(() => {
    setHasError(false);
    setIsFetching(true);

    messengerApi
      .fetch(messengerId)
      .then((settings) => {
        const boardId = settings.settings?.integrations?.bulletinBoard?.boardId;
        if (!boardId) throw new Error('boardId not found');

        setBoardId(boardId);
        return boardId;
      })
      .then((boardId) =>
        fetchBulletin({
          boardId,
          bulletinId,
        }),
      )
      .then(() => setIsFetching(false))
      .catch(() => {
        setIsFetching(false);
        setHasError(true);
      });
  }, [messengerId, bulletinId, fetchBulletin, messengerApi]);

  return {
    isFetching: isFetching || fetchBulletinIsFetching,
    hasError: hasError || isError,
    bulletin: bulletinApiData || null,
    bulletinBoardId: boardId,
  };
}
